import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Document, DocumentTypeEnum } from '@/common/types';

const isCanvasCompletelyWhite = (canvas: HTMLCanvasElement): boolean => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return false;
  }
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // completely white + completely opaque.
    if (!(r === 255 && g === 255 && b === 255 && a === 255)) {
      return false;
    }
  }

  return true;
};

interface Props {
  url?: string;
}

const useConvertPdfToPng = ({ url }: Props) => {
  const [pngUrls, setPngUrls] = useState<Array<string | undefined>>();
  const [isLoading, setIsLoading] = useState(!!url);
  const [error, setError] = useState<string | null>(null);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [isPasswordIncorrect, setIsPasswordIncorrect] = useState(false);
  const [passwordEntry, setPasswordEntry] = useState<{ url?: string; value: string }>();
  const password = passwordEntry && passwordEntry.url === url ? passwordEntry.value : undefined;
  const pdfDataRef = useRef<{ url: string; data: ArrayBuffer } | undefined>(undefined);

  const initializePdfJs = useCallback(async () => {
    const pdfjsLib = await import('pdfjs-dist');
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();
    }
    return pdfjsLib;
  }, []);

  const decodePdfDocument = useCallback(
    async (source?: string) => {
      if (!source) return undefined;

      const pdfjsLib = await initializePdfJs();

      if (pdfDataRef.current?.url !== source) {
        const response = await fetch(source, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            Accept: 'application/pdf,*/*',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        pdfDataRef.current = { url: source, data: await response.arrayBuffer() };
      }

      const loadingTask = pdfjsLib.getDocument({ data: pdfDataRef.current.data.slice(0), password });
      return await loadingTask.promise;
    },
    [initializePdfJs, password]
  );

  const convertPdfPageToPng = useCallback(
    async (pageIndex: number, pdf: any): Promise<string | undefined> => {
      try {
        const page = await pdf.getPage(pageIndex + 1);
        const viewport = page.getViewport({ scale: 2 });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          return undefined;
        }

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        };

        await page.render(renderContext).promise;

        const dataUrl = canvas.toDataURL('image/png', 0.95);

        const hasError = isCanvasCompletelyWhite(canvas);
        if (hasError) return;

        return dataUrl;
      } catch (pageError) {
        return undefined;
      }
    },
    []
  );

  const getPngUrls = useCallback(
    async (source?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const pdf = await decodePdfDocument(source);

        if (pdf) {
          const totalPages = pdf.numPages;
          const batchSize = 3;
          const urls: (string | undefined)[] = [];

          for (let i = 0; i < totalPages; i += batchSize) {
            const batch = Array.from({ length: Math.min(batchSize, totalPages - i) }, (_, j) =>
              convertPdfPageToPng(i + j, pdf)
            );

            const batchResults = await Promise.all(batch);
            urls.push(...batchResults);

            if (i + batchSize < totalPages) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }

          setPngUrls(urls);
        }

        setIsPasswordProtected(false);
        setIsPasswordIncorrect(false);
        setIsLoading(false);
      } catch (e) {
        if (e instanceof Error && e.name === 'PasswordException') {
          setIsPasswordProtected(true);
          setIsPasswordIncorrect(!!password);
        } else {
          setError(e instanceof Error ? e.message : 'Unknown error occurred');
        }
        setIsLoading(false);
      }
    },
    [decodePdfDocument, convertPdfPageToPng, password]
  );

  const submitPassword = useCallback(
    (newPassword: string) => {
      setPasswordEntry({ url, value: newPassword });
    },
    [url]
  );

  useEffect(() => {
    if (url) {
      getPngUrls(url);
    }
  }, [url, getPngUrls]);

  const pngDocuments: Document[] = useMemo(() => {
    if (!pngUrls?.length) return [];

    return pngUrls
      .map((pngUrl, index) => {
        if (!pngUrl) return null;
        return {
          blob: pngUrl,
          link: pngUrl,
          id: `page-${index}`,
          type: DocumentTypeEnum.PNG,
        };
      })
      .filter(Boolean) as Document[];
  }, [pngUrls]);

  return {
    pngUrls: pngUrls?.filter(Boolean) || [],
    pngDocuments,
    isLoading,
    error,
    isPasswordProtected,
    isPasswordIncorrect,
    submitPassword,
    totalPages: pngUrls?.length || 0,
    successfulPages: pngUrls?.filter(Boolean).length || 0,
  };
};

export default useConvertPdfToPng;
